import { twMerge } from 'tailwind-merge';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import moment from 'moment';
import { useAppSelector } from 'flow/hooks/useRedux';
import { groupListBy } from 'flow/utils';
import {
    TicketEvent,
    TicketEventKey,
    eventIsCallRecordEvent,
} from 'flow/types/TicketEventData';
import { TicketKey } from 'flow/types/EntityKeys';
import { TypedMap } from 'flow/types/TypedMap';
import { selectOriginDTOSubtype } from 'flow/selectors/TicketsSelectors';
import DateSectionHeader from 'flow/components/pbx/v2/detail-page/call-event/DateSectionHeader';
import EventsListItemWrapperV2 from './EventsListItemWrapperV2';

const handleScrollOnNewItem = (
    anchorRef: React.MutableRefObject<HTMLDivElement | null>,
) => {
    anchorRef?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
    });
};

interface Props {
    ticketKey: TicketKey;
    ticketEvents: TypedMap<Record<TicketEventKey, TypedMap<TicketEvent>>>;
    discardCallEvents?: boolean;
    discriminatorDate?: string;
    dateLabelBgClassName?: string;
}

const EventsListV2 = ({
    ticketKey,
    ticketEvents,
    discardCallEvents,
    discriminatorDate,
    dateLabelBgClassName,
}: Props) => {
    const dtoSubtype = useAppSelector((state) =>
        selectOriginDTOSubtype(state, ticketKey),
    );
    const anchorRef = useRef<HTMLDivElement | null>(null);
    const ticketEventsSizePrev = useRef(ticketEvents?.size);
    const [contentIsReadyForDisplay, setContentIsReadyForDisplay] = useState(
        dtoSubtype !== 'email',
    );

    useEffect(() => {
        setContentIsReadyForDisplay(dtoSubtype !== 'email');
        let timeoutId: ReturnType<typeof setTimeout> | undefined;
        if (dtoSubtype === 'email') {
            timeoutId = setTimeout(() => {
                setContentIsReadyForDisplay(true);
            }, 0);
        }
        return () => {
            if (timeoutId !== undefined) clearTimeout(timeoutId);
        };
    }, [dtoSubtype, ticketKey]);

    useEffect(() => {
        // The scroll container is an ancestor (in ConversationTabContent), not
        // this component's own element, so jump the bottom anchor into view
        // rather than setting scrollTop on a non-scrolling node.
        if (anchorRef?.current && contentIsReadyForDisplay) {
            anchorRef.current.scrollIntoView({
                behavior: 'instant',
                block: 'end',
            });
        }
    }, [ticketKey, contentIsReadyForDisplay]);

    useEffect(() => {
        if (ticketEvents?.size > ticketEventsSizePrev?.current) {
            handleScrollOnNewItem(anchorRef);
            ticketEventsSizePrev.current = ticketEvents.size;
        }
    }, [ticketEvents, ticketEventsSizePrev]);

    const onNewAttachmentsFetched = useCallback(
        () => handleScrollOnNewItem(anchorRef),
        [],
    );

    const ticketEventsSorted = useMemo(() => {
        const filtered = ticketEvents?.filter((event) => {
            // The reducer stores only events the client can show.
            return !(
                discardCallEvents &&
                eventIsCallRecordEvent(event.get('DTOSubtype'))
            );
        });
        return filtered
            ?.sort((a, b) =>
                moment(a.get('timeStamp')).isBefore(moment(b.get('timeStamp')))
                    ? -1
                    : 1,
            )
            .toList();
    }, [ticketEvents, discardCallEvents]);

    const eventsSections = useMemo(() => {
        // Each call is now a single consolidated event, so it buckets by its own
        // timestamp like any other event — no call-aware sectioning/regrouping needed.
        const groupedByDate = groupListBy(
            ticketEventsSorted,
            (event: TypedMap<TicketEvent>) =>
                moment(event.get('timeStamp')).format('YYYY-MM-DD'),
        );
        const sortedDateKeys = groupedByDate.keySeq().sort();

        const discriminatorDateKey = discriminatorDate
            ? moment(discriminatorDate).format('YYYY-MM-DD')
            : null;

        // One wrapper per day, holding that day's events. getMessageGroups (used by
        // the chat lists) is deliberately not applied here: it coalesces on `sent`
        // and `from`, which no ticket event carries — OmniTicketEventDTO has only
        // timeStamp, messages use senderData — so it always returned a single group
        // per day anyway. Its one real consumer was the call-fragment regrouping,
        // gone now that a call is one event.
        return sortedDateKeys.map((date: string) => (
            <div key={`el-datesection-${date}`} className="w-full">
                {date !== discriminatorDateKey && (
                    <DateSectionHeader
                        date={date}
                        bgClassName={dateLabelBgClassName}
                    />
                )}
                <EventsListItemWrapperV2
                    ticketKey={ticketKey}
                    events={groupedByDate.get(date)}
                    onNewAttachmentsFetched={onNewAttachmentsFetched}
                />
            </div>
        ));
    }, [
        ticketEventsSorted,
        discriminatorDate,
        dateLabelBgClassName,
        ticketKey,
        onNewAttachmentsFetched,
    ]);

    return (
        <div
            className={twMerge(
                'antialiased',
                contentIsReadyForDisplay
                    ? 'opacity-100 transition-opacity duration-100'
                    : 'pointer-events-none opacity-0',
            )}
        >
            {eventsSections}
            <div ref={anchorRef} />
        </div>
    );
};

export default EventsListV2;
